import React, { useState, useEffect, useRef, useCallback } from "react";
import myAxios from "../../_services/axiosilearnWrapper";
import Config from "../../_config/config";
import "./CaseDiscussionOverlay.css";

const POLL_INTERVAL_MS = 3000;

/**
 * Renders one case-discussion panel for a given active session. The parent
 * (StudentAssistFab) decides which sessionId is currently open — this
 * component polls only that session's turns and handles the send/close UX.
 *
 * Props:
 *   session   — active session summary { sessionId, groupName, caseTitle, caseText, ... }
 *   enquiryId — student's enquiry id (for actorEnquiryId when responding)
 *   onClose   — invoked when the student closes/minimizes the panel
 *   onTurnsSeen — invoked with the latest TurnIdx once the parent should
 *                 consider this session "read" (used by the FAB badge)
 */
const CaseDiscussionOverlay = ({ session, enquiryId, onClose, onTurnsSeen }) => {
  const [turns, setTurns] = useState([]);
  const [status, setStatus] = useState("active");
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [caseExpanded, setCaseExpanded] = useState(false);

  const lastTurnIdxRef = useRef(-1);
  const turnPollRef = useRef(null);
  const threadEndRef = useRef(null);

  const sessionId = session?.sessionId;

  const pollForTurns = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await myAxios.get(
        `${Config.nodeSiteUrl}v1/flip-class/case-discussion/${sessionId}/turns`,
        { params: { sinceTurnIdx: lastTurnIdxRef.current } }
      );
      const data = res.data;
      if (data?.success) {
        const newTurns = data.data.turns || [];
        if (newTurns.length > 0) {
          setTurns((prev) => [...prev, ...newTurns]);
          lastTurnIdxRef.current = newTurns[newTurns.length - 1].TurnIdx;
          if (onTurnsSeen) onTurnsSeen(sessionId, lastTurnIdxRef.current);
        }
        setStatus(data.data.status);
        if (data.data.status === "closed" && turnPollRef.current) {
          clearInterval(turnPollRef.current);
          turnPollRef.current = null;
        }
      }
    } catch (e) {
      // Silent — student polling shouldn't surface auth blips
    }
  }, [sessionId, onTurnsSeen]);

  // Reset & start polling whenever the session changes
  useEffect(() => {
    setTurns([]);
    setStatus("active");
    setDraft("");
    setError(null);
    lastTurnIdxRef.current = -1;
    if (turnPollRef.current) {
      clearInterval(turnPollRef.current);
      turnPollRef.current = null;
    }
    if (!sessionId) return undefined;
    pollForTurns();
    turnPollRef.current = setInterval(pollForTurns, POLL_INTERVAL_MS);
    return () => {
      if (turnPollRef.current) {
        clearInterval(turnPollRef.current);
        turnPollRef.current = null;
      }
    };
  }, [sessionId, pollForTurns]);

  useEffect(() => {
    if (threadEndRef.current) {
      threadEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [turns]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || submitting || status !== "active" || !sessionId) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await myAxios.post(
        `${Config.nodeSiteUrl}v1/flip-class/case-discussion/${sessionId}/respond`,
        { content: text, actorEnquiryId: enquiryId }
      );
      if (res.data?.success) {
        const allTurns = res.data.data.turns || [];
        setTurns(allTurns);
        if (allTurns.length > 0) {
          lastTurnIdxRef.current = allTurns[allTurns.length - 1].TurnIdx;
          if (onTurnsSeen) onTurnsSeen(sessionId, lastTurnIdxRef.current);
        }
        setDraft("");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Could not send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!session) return null;

  const isClosed = status === "closed";

  return (
    <div className="case-discussion-overlay">
      <div className="cdo-card">
        <div className="cdo-header">
          <div className="cdo-header-left">
            <span className="cdo-group-pill">{session.groupName}</span>
            <span className="cdo-title">Case Discussion</span>
          </div>
          <div className="cdo-header-right">
            {isClosed && <span className="cdo-status-closed">Closed</span>}
            <button
              type="button"
              className="cdo-close"
              onClick={onClose}
              aria-label="Close discussion"
              title="Close"
            >
              −
            </button>
          </div>
        </div>

        <div className={`cdo-case ${caseExpanded ? "is-expanded" : ""}`}>
          <div className="cdo-case-header">
            <strong>{session.caseTitle}</strong>
            <button
              type="button"
              className="cdo-case-toggle"
              onClick={() => setCaseExpanded((v) => !v)}
            >
              {caseExpanded ? "Hide case" : "Read case"}
            </button>
          </div>
          {caseExpanded && (
            <div className="cdo-case-text">{session.caseText}</div>
          )}
        </div>

        <div className="cdo-thread">
          {turns.length === 0 && (
            <div className="cdo-loading">Loading discussion…</div>
          )}
          {turns.map((t) => (
            <div
              key={t.Id}
              className={`cdo-turn cdo-turn-${t.ActorType}`}
            >
              <div className="cdo-turn-actor">
                {t.ActorType === "ai" && "🤖 Tutor"}
                {t.ActorType === "student" && "You / Group-mate"}
                {t.ActorType === "teacher" && "👋 Teacher"}
              </div>
              <div className="cdo-turn-content">{t.Content}</div>
            </div>
          ))}
          <div ref={threadEndRef} />
        </div>

        {error && <div className="cdo-error">{error}</div>}

        {!isClosed && (
          <form className="cdo-input-row" onSubmit={handleSubmit}>
            <textarea
              className="cdo-input"
              placeholder="Type your group's response… (anyone in your group can answer)"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={2}
              disabled={submitting}
              maxLength={2000}
            />
            <button
              type="submit"
              className="cdo-send"
              disabled={submitting || !draft.trim()}
            >
              {submitting ? "Sending…" : "Send"}
            </button>
          </form>
        )}

        {isClosed && (
          <div className="cdo-closed-banner">
            This discussion has been closed by your teacher. You can revisit the transcript afterwards.
          </div>
        )}
      </div>
    </div>
  );
};

export default CaseDiscussionOverlay;
